import { useState, useEffect, useCallback } from 'react';
import { formService } from '../../../services/formService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UsePendingSubmissionsCountResult {
  pendingCount: number;
  /** Pending submissions waiting more than 48 hours */
  pendingOver48hCount: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface PendingSubmissionsFilter {
  serviceArea?: string;
  foremanArea?: string;
}

/**
 * Fetches the pending submissions count from the same API as the Submissions page
 * (formService.getFilterCounts). When filter is provided, counts are scoped to that area.
 */
export function usePendingSubmissionsCount(
  enabled: boolean,
  filter?: PendingSubmissionsFilter | null
): UsePendingSubmissionsCountResult {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingOver48hCount, setPendingOver48hCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const filterKey = filter
    ? `${filter.serviceArea ?? ''}-${filter.foremanArea ?? ''}`
    : '';

  const fetchCount = useCallback(async () => {
    if (!enabled) {
      setPendingCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: { serviceArea?: string; foremanArea?: string } = {};
      if (filter?.serviceArea) params.serviceArea = filter.serviceArea;
      if (filter?.foremanArea) params.foremanArea = filter.foremanArea;
      const counts = await queueHomeRequest(() => formService.getFilterCounts(params));
      setPendingCount(counts.statusCounts.pending ?? 0);
      setPendingOver48hCount(counts.pendingOver48h ?? 0);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch pending count')
      );
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [enabled, filterKey]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { pendingCount, pendingOver48hCount, loading, error, refetch: fetchCount };
}
